import { h } from 'vue'
import { configureRichText } from '@addictivegroup/addictive-richtext/core'
import i18n from '@/i18n/index.js'
import UiMenu from '@/components/ui/UiMenu.vue'

/**
 * Text formatting in AddCRM (D-232, D-251, D-150, D-170) through addictive-richtext, drawn as the
 * panel draws it: its Tailwind classes per part, Font Awesome icons, its own dropdown (UiMenu).
 * The words are the library's (the panel's own were the same).
 */
const FA = {
  bold: 'fa-bold', italic: 'fa-italic', underline: 'fa-underline', strike: 'fa-strikethrough', code: 'fa-code', quote: 'fa-quote-left', rule: 'fa-minus',
  bullets: 'fa-list-ul', numbers: 'fa-list-ol', link: 'fa-link', table: 'fa-table', tableTools: 'fa-table-cells', left: 'fa-align-left', center: 'fa-align-center',
  right: 'fa-align-right', justify: 'fa-align-justify', clearFormatting: 'fa-text-slash', emphasis: 'fa-droplet', colors: 'fa-font', more: 'fa-ellipsis',
  paragraph: 'fa-paragraph', chevronDown: 'fa-chevron-down', chevronRight: 'fa-chevron-right', check: 'fa-check', blank: 'fa-fw', none: 'fa-ban',
  cut: 'fa-scissors', copy: 'fa-copy', paste: 'fa-paste', columnBefore: 'fa-arrow-left', columnAfter: 'fa-arrow-right', rowBefore: 'fa-arrow-up',
  fields: 'fa-code', image: 'fa-image', spinner: 'fa-circle-notch fa-spin', spelling: 'fa-spell-check', ignore: 'fa-eye-slash', dictionary: 'fa-book', undo: 'fa-rotate-left', redo: 'fa-rotate-right', rowAfter: 'fa-arrow-down', deleteColumn: 'fa-table-columns', deleteRow: 'fa-grip-lines', headerRow: 'fa-heading', deleteTable: 'fa-trash-can',
}
// A name of the library's, or the panel's own Font Awesome name (items the panel adds to a menu).
const fa = (name) => (name?.startsWith('fa-') ? name : FA[name] || name)
const ICON_CLASSES = {
  'button-field': 'fa-solid fa-fw', 'button-page': 'fa-solid', swatch: 'fa-solid text-faint', styleChevron: 'fa-solid text-3xs',
  contextItem: 'fa-solid fa-fw text-xs', contextChevron: 'fa-solid text-3xs text-faint', suggestItem: 'fa-solid fa-fw text-faint', figureTitle: 'fa-solid',
}

// Every button of a field's toolbar one size, its icon centred: the same height, the same width at least.
const BTN = 'h-7 min-w-7 items-center justify-center rounded px-1 text-xs sm:px-1.5'
const ON = 'bg-brand-100 text-brand-800 dark:bg-brand-900/60 dark:text-brand-100'
const OFF = 'text-muted hover:bg-[var(--surface-3)]'
const RING = 'border-[var(--focus)] ring-1 ring-[var(--focus)]'
export const RICHTEXT_CLASSES = {
  toolbar: ({ variant }) => (variant === 'page' ? 'flex flex-wrap items-center gap-1 px-3 py-1' : 'rte-toolbar flex items-center gap-0.5 overflow-hidden border-b border-default px-1.5 py-1'),
  separator: ({ variant }) => (variant === 'page' ? 'mx-1 h-5 w-px bg-[var(--border)]' : 'mx-0.5 h-4 w-px shrink-0 sm:mx-1 bg-[var(--border)]'),
  button: ({ variant, active }) => (variant === 'page' ? `rounded px-1.5 py-1 text-xs disabled:opacity-40 ${active ? ON : OFF}` : `inline-flex ${BTN} ${active ? ON : OFF}`),
  buttonText: 'font-semibold',
  styleSelect: 'input w-auto min-w-[8rem] py-0.5 pr-8 text-xs',
  styleButton: `flex gap-1 ${BTN}`,
  styleLabel: 'max-w-24 truncate',
  colorsButton: `flex ${BTN}`,
  colorsGlyph: 'relative inline-flex',
  colorsBar: ({ highlight }) => `absolute -bottom-1 left-0 right-0 h-[3px] rounded-sm${highlight ? '' : ' bg-current'}`,
  tableButton: `flex ${BTN} bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200`,
  moreButton: `flex ${BTN}`,
  fieldsButton: ({ variant }) => (variant === 'page' ? 'inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs' : `ml-auto flex gap-1 ${BTN}`),
  imageButton: ({ variant, disabled }) => (variant === 'page' ? `rounded px-1.5 py-1 text-xs text-muted hover:bg-[var(--surface-3)] ${disabled ? 'pointer-events-none opacity-40' : 'cursor-pointer'}` : `inline-flex cursor-pointer text-muted hover:bg-[var(--surface-3)] ${BTN}`),
  figurePanel: 'border-t border-default bg-[var(--surface-2)] px-3 py-2',
  figureTitle: 'mb-1.5 text-xs font-semibold text-muted',
  figureGrid: 'grid gap-2 sm:grid-cols-[1fr_1fr_8rem]',
  figureLabel: 'block text-xs text-muted',
  figureInput: 'input mt-0.5 py-1 text-sm',
  suggestList: 'surface max-h-64 w-72 overflow-y-auto rounded-xl border border-default py-1 shadow-xl',
  suggestEmpty: 'px-3 py-2 text-sm text-faint',
  suggestItem: ({ active }) => `flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm${active ? ' bg-brand-50 dark:bg-brand-900/40' : ''}`,
  suggestLabel: 'min-w-0 flex-1 truncate',
  suggestHint: 'text-xs text-faint',
  view: 'prose-crm',
  viewTable: 'rich-table',
  viewFallback: 'whitespace-pre-line',
  palette: 'w-64 px-3 py-2 text-xs',
  paletteTitle: 'mb-1.5 font-semibold text-faint',
  paletteRow: ({ kind }) => (kind === 'text' ? 'mb-3 flex flex-wrap gap-1.5' : 'flex flex-wrap gap-1.5'),
  swatch: ({ kind, active, none }) => (none ? 'grid h-7 w-7 place-items-center rounded border border-default'
    : kind === 'text' ? `grid h-7 w-7 place-items-center rounded border font-bold ${active ? RING : 'border-default'}` : `h-7 w-7 rounded border ${active ? RING : 'border-default'}`),
  contextMenu: 'surface max-h-[calc(100vh-1rem)] min-w-[15rem] max-w-[22rem] overflow-visible rounded-xl border border-default py-1 text-sm shadow-xl',
  contextSub: 'surface max-h-[min(24rem,calc(100vh-1rem))] min-w-[13rem] max-w-[22rem] overflow-y-auto overscroll-contain rounded-xl border border-default py-1 shadow-xl',
  contextDivider: 'my-1 h-px bg-[var(--border)]',
  contextHeader: ({ sub }) => (sub ? 'px-3 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wide text-faint' : 'truncate px-3 pb-1 pt-1.5 text-2xs font-semibold uppercase tracking-wide text-faint'),
  contextItem: ({ on, danger, strong }) => ['flex w-full items-center gap-2 px-3 py-1.5 text-left disabled:opacity-50', on ? 'bg-brand-50 text-brand-800 dark:bg-brand-900/40 dark:text-brand-100' : 'hover:bg-[var(--surface-3)]', danger ? 'text-red-600' : '', strong ? 'font-semibold' : ''].filter(Boolean).join(' '),
  contextText: 'w-4 text-center text-2xs font-bold',
  contextLabel: 'min-w-0 flex-1 truncate',
  contextKeys: 'text-2xs text-faint',
  contextHint: 'font-mono text-2xs text-faint',
}

/**
 * The fields' values on the offer's page (D-171) as the offer prints them: drawn with the page's
 * classes and paragraph style; a place for equipment no piece fills prints nothing (D-157).
 */
export const offerFieldValues = (values) => ({
  values,
  classes: { field: 'doc-ph', value: 'doc-ph-value', list: 'doc-ph-list', line: 'doc-ph-line', spec: 'doc-ph-spec' },
  lineClass: 'p-body',
  emptyLines: (key) => /^component\.equipment(\.|$)/i.test(key),
})

/** What changed in an offer's text since a revision (D-154, D-168), drawn as a marked revision prints it. */
export const offerChangeMarks = (changes, key, values) => ({
  changes,
  key,
  values,
  classes: { changed: 'changed', removed: 'removed', removedBlock: 'doc-removed', removedWords: 'doc-removed-words' },
  lineClass: 'p-body',
})

// The pictures' words as the offer's print has them ("printed as a figure", "Figure N. …").
const OWN_WORDS = {
  insertImage: 'offers.insertImage', figureSelected: 'offers.figureSelected', figureCaption: 'offers.figureCaption', figureCaptionHint: 'offers.figureCaptionHint',
  figureSource: 'offers.figureSource', figureSourceHint: 'offers.figureSourceHint', figureWidth: 'offers.figureWidthLabel', figureWidthHint: 'offers.figureWidthHint',
}

export function setupRichText() {
  configureRichText({
    locale: () => i18n.global.locale.value,
    classes: RICHTEXT_CLASSES,
    icon: (name, part) => h('i', { class: [ICON_CLASSES[part] || 'fa-solid fa-fw', fa(name)], 'aria-hidden': 'true' }),
    iconName: fa,
    components: { menu: UiMenu },
    translate: (key, params) => (OWN_WORDS[key] ? i18n.global.t(OWN_WORDS[key], params || {}) : null),
  })
}
